import { useEffect, useRef, useState } from 'react';

// audioLevel is on the 0-100 scale.
const SOUND_DETECTION_THRESHOLD = 1;
const SOUND_CHECK_TIMEOUT_MS = 5000;

type SoundCheckStatus = 'idle' | 'checking' | 'detected' | 'no-sound';

export const useSoundCheck = (audioLevel: number, isActive: boolean) => {
  const [status, setStatus] = useState<SoundCheckStatus>('idle');
  const timeoutRef = useRef<number | null>(null);

  // status is intentionally omitted from deps — including it would
  // re-trigger the timeout on every status change
  useEffect(() => {
    if (!isActive) {
      setStatus('idle');

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      return;
    }

    if (status === 'idle') {
      setStatus('checking');

      timeoutRef.current = window.setTimeout(() => {
        setStatus((current) => {
          if (current === 'checking') {
            return 'no-sound';
          }

          return current;
        });
      }, SOUND_CHECK_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isActive]);

  useEffect(() => {
    if (
      (status === 'checking' || status === 'no-sound') &&
      audioLevel > SOUND_DETECTION_THRESHOLD
    ) {
      setStatus('detected');

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [audioLevel, status]);

  return { soundCheckStatus: status };
};
